import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

const CaseStudyModal = ({ caseStudy, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');

  if (!isOpen || !caseStudy) return null;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'FileText' },
    { id: 'solution', label: 'Solution', icon: 'Settings' },
    { id: 'results', label: 'Results', icon: 'TrendingUp' },
    { id: 'testimonials', label: 'Testimonials', icon: 'MessageSquare' }
  ];

  const performanceData = [
    { name: 'Before', efficiency: 45, accuracy: 72, cost: 100 },
    { name: 'Month 1', efficiency: 55, accuracy: 78, cost: 85 },
    { name: 'Month 3', efficiency: 68, accuracy: 82, cost: 70 },
    { name: 'Month 6', efficiency: 75, accuracy: 88, cost: 55 },
    { name: 'Current', efficiency: 85, accuracy: 95, cost: 40 }
  ];

  const implementationTimeline = [
    { phase: 'Discovery', duration: '2 weeks', status: 'completed' },
    { phase: 'Architecture', duration: '3 weeks', status: 'completed' },
    { phase: 'Development', duration: '8 weeks', status: 'completed' },
    { phase: 'Testing', duration: '2 weeks', status: 'completed' },
    { phase: 'Deployment', duration: '1 week', status: 'completed' },
    { phase: 'Optimization', duration: 'Ongoing', status: 'active' }
  ];

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      {/* Modal */}
      <div className="absolute inset-4 bg-white rounded-lg enterprise-shadow overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-4">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${caseStudy?.company === 'DIEXTRA App' ? 'bg-white' : 'bg-surface'}`}>
              <Image 
                src={caseStudy?.companyLogo} 
                alt={`${caseStudy?.company} logo`}
                className="w-8 h-8 object-contain"
              />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-text-primary">{caseStudy?.title}</h2>
              <p className="text-text-secondary">{caseStudy?.company} • {caseStudy?.industry}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
          />
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center space-x-2 px-6 py-3 text-sm font-medium enterprise-transition ${
                activeTab === tab?.id
                  ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-text-secondary hover:text-primary hover:bg-surface'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {activeTab === 'overview' && (
            <div className="p-6 space-y-6">
              {/* Challenge */}
              <div>
                <h3 className="text-lg font-semibold text-text-primary mb-3 flex items-center space-x-2">
                  <Icon name="AlertTriangle" size={20} className="text-warning" />
                  <span>Challenge</span>
                </h3>
                <p className="text-text-secondary leading-relaxed">
                  {caseStudy?.challenge}
                </p>
              </div>

              {/* Project Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-surface rounded-lg p-4">
                  <h4 className="font-medium text-text-primary mb-3">Project Details</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Timeline:</span>
                      <span className="text-text-primary">{caseStudy?.timeline}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Team Size:</span>
                      <span className="text-text-primary">{caseStudy?.teamSize}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Investment:</span>
                      <span className="text-text-primary">{caseStudy?.investment}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-secondary">ROI:</span>
                      <span className="text-success font-medium">{caseStudy?.roi}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-surface rounded-lg p-4">
                  <h4 className="font-medium text-text-primary mb-3">Key Metrics</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {caseStudy?.keyMetrics?.map((metric, index) => (
                      <div key={index} className="text-center">
                        <div className="text-2xl font-bold text-primary mb-1">
                          {metric?.value}
                        </div>
                        <div className="text-xs text-text-secondary">
                          {metric?.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Technologies */}
              <div>
                <h4 className="font-medium text-text-primary mb-3">Technologies Used</h4>
                <div className="flex flex-wrap gap-2">
                  {caseStudy?.technologies?.map((tech, index) => (
                    <span key={index} className="px-3 py-1 bg-primary/10 text-primary text-sm rounded-full">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'solution' && (
            <div className="p-6 space-y-6">
              {/* Solution Architecture */}
              <div>
                <h3 className="text-lg font-semibold text-text-primary mb-4">Solution Architecture</h3>
                <p className="text-text-secondary leading-relaxed mb-4">
                  {caseStudy?.solutionDescription}
                </p>
                
                {/* Architecture Diagram Placeholder */}
                <div className="bg-surface rounded-lg p-8 text-center">
                  <Icon name="Network" size={48} className="text-text-secondary mx-auto mb-4" />
                  <p className="text-text-secondary">Interactive Architecture Diagram</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Detailed system architecture and data flow visualization
                  </p>
                </div>
              </div>

              {/* Implementation Timeline */}
              <div>
                <h4 className="font-medium text-text-primary mb-4">Implementation Timeline</h4>
                <div className="space-y-3">
                  {implementationTimeline?.map((phase, index) => (
                    <div key={index} className="flex items-center space-x-4 p-3 bg-surface rounded-lg">
                      <div className={`w-3 h-3 rounded-full ${
                        phase?.status === 'completed' ? 'bg-success' :
                        phase?.status === 'active' ? 'bg-primary' : 'bg-gray-300'
                      }`} />
                      <div className="flex-1">
                        <div className="font-medium text-text-primary">{phase?.phase}</div>
                        <div className="text-sm text-text-secondary">{phase?.duration}</div>
                      </div>
                      <div className={`px-2 py-1 rounded-full text-xs ${
                        phase?.status === 'completed' ? 'bg-success/10 text-success' :
                        phase?.status === 'active' ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {phase?.status}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'results' && (
            <div className="p-6 space-y-6">
              {/* Performance Chart */}
              <div>
                <h3 className="text-lg font-semibold text-text-primary mb-4">Performance Improvement</h3>
                <div className="bg-surface rounded-lg p-4">
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={performanceData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="efficiency" stroke="#3182ce" strokeWidth={2} name="Efficiency %" />
                      <Line type="monotone" dataKey="accuracy" stroke="#38a169" strokeWidth={2} name="Accuracy %" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Cost Reduction Chart */}
              <div>
                <h4 className="font-medium text-text-primary mb-4">Cost Reduction Over Time</h4>
                <div className="bg-surface rounded-lg p-4">
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={performanceData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="cost" fill="#e53e3e" name="Cost Index" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Key Achievements */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {caseStudy?.achievements?.map((achievement, index) => (
                  <div key={index} className="bg-success/10 border border-success/20 rounded-lg p-4 text-center">
                    <Icon name="Award" size={24} className="text-success mx-auto mb-2" />
                    <div className="text-lg font-bold text-success mb-1">{achievement?.value}</div>
                    <div className="text-sm text-text-secondary">{achievement?.label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'testimonials' && (
            <div className="p-6 space-y-6">
              {/* Main Testimonial */}
              {caseStudy?.testimonial && (
                <div className="bg-surface rounded-lg p-6">
                  <div className="flex items-start space-x-4">
                    <Image 
                      src={caseStudy?.testimonial?.avatar} 
                      alt={caseStudy?.testimonial?.name}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    <div className="flex-1">
                      <blockquote className="text-lg text-text-primary italic mb-4">
                        "{caseStudy?.testimonial?.fullQuote || caseStudy?.testimonial?.quote}"
                      </blockquote>
                      <div className="flex items-center space-x-4">
                        <div>
                          <div className="font-semibold text-text-primary">{caseStudy?.testimonial?.name}</div>
                          <div className="text-text-secondary">{caseStudy?.testimonial?.title}</div>
                          <div className="text-sm text-muted-foreground">{caseStudy?.company}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Testimonials */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {caseStudy?.additionalTestimonials?.map((testimonial, index) => (
                  <div key={index} className="bg-white border border-border rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <Image 
                        src={testimonial?.avatar} 
                        alt={testimonial?.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div className="flex-1">
                        <p className="text-sm text-text-secondary italic mb-2">
                          "{testimonial?.quote}"
                        </p>
                        <div className="text-xs">
                          <div className="font-medium text-text-primary">{testimonial?.name}</div>
                          <div className="text-muted-foreground">{testimonial?.title}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border bg-surface">
          <div className="flex items-center space-x-4">
            <Button variant="outline" iconName="Download" iconPosition="left">
              Download PDF
            </Button>
            <Button variant="ghost" iconName="Share2" iconPosition="left">
              Share Case Study
            </Button>
          </div>
          <Button variant="default" iconName="MessageCircle" iconPosition="left">
            Discuss Similar Project
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CaseStudyModal;